import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * PerpsPricingStrip — 3-column mini ticker (BTC / STX / STXOSHI PERP)
 * shown on the Landing page just above the footer. Tap any card to open
 * the full Perps trading UI on that market.
 *
 * Reuses the exact same /api/perps/markets endpoint as the Perps page so
 * price + 24h change stay in perfect sync between the two surfaces.
 */
function fmtPrice(market, price) {
  const p = Number(price) || 0;
  if (market === "BTC") {
    return `$${p.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  }
  if (market === "STX") {
    return `$${p.toLocaleString(undefined, { maximumFractionDigits: 4 })}`;
  }
  // STXOSHI — sub-cent. Show 4-sig-fig scientific to save width on mobile.
  if (p === 0) return "—";
  return `$${p.toExponential(4)}`;
}

export default function PerpsPricingStrip() {
  const navigate = useNavigate();
  const [markets, setMarkets] = useState([]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        // cache:no-store defeats iOS Safari's aggressive cache so the strip
        // never lingers on stale price data after a redeploy.
        const r = await fetch(`${API}/api/perps/markets?_=${Date.now()}`, {
          cache: "no-store",
        });
        // Guard: skip parse when server returns a non-JSON 5xx (Cloudflare
        // 520 / 502 / 503 during pod restarts) so JSON.parse doesn't throw
        // "The string did not match the expected pattern" in the browser.
        if (!r.ok || !(r.headers.get("content-type") || "").includes("json")) return;
        const d = await r.json();
        if (alive) setMarkets(d.markets || []);
      } catch { /* silent */ }
    };
    load();
    const t = setInterval(load, 15000); // refresh with the same cadence as Perps
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  if (!markets.length) return null;

  return (
    <div className="mt-8 max-w-2xl mx-auto" data-testid="landing-perps-strip">
      <div className="grid grid-cols-3 gap-2">
        {markets.map((m) => {
          const positive = (m.change_24h_pct || 0) >= 0;
          return (
            <button
              key={m.market}
              onClick={() => navigate("/perps")}
              className="min-w-0 px-2 sm:px-4 py-2.5 rounded-lg border border-border hover:border-orange-500/50 bg-card/40 backdrop-blur transition-all text-left"
              data-testid={`landing-perp-${m.market}`}
            >
              <div className="flex items-center gap-1 mb-0.5 min-w-0">
                <span className="font-heading text-[11px] sm:text-sm font-bold truncate">
                  {m.symbol}
                </span>
              </div>
              <div className="font-mono text-[10px] sm:text-xs truncate">
                {fmtPrice(m.market, m.price_usd)}
              </div>
              <div className={`text-[9px] sm:text-[10px] font-mono truncate ${positive ? "text-green-400" : "text-red-400"}`}>
                {positive ? "+" : ""}
                {(m.change_24h_pct || 0).toFixed(2)}% 24h
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
